// The snapping choices a customer makes in the room planner's top bar
// (CO-5856): Snapping, with its Faces and Corners options, Align (lining up
// across a gap) and Guides. A customer starts with everything on, and the
// choices are kept in this browser between sessions. Storage can be missing
// or refuse access (a private window, blocked site data, tests), in which
// case a choice lasts the session and everything starts on.

const STORAGE_KEY = '3drp:snapping';

/**
 * The snapping choices.
 */
export interface SnapSettings {
    // Snapping as a whole, product rules included; faces and corners only
    // apply while it is on.
    snapping: boolean;
    faces: boolean;
    corners: boolean;
    // Lining up across a gap, whether snapping is on or off.
    align: boolean;
    // Whether the guides show while dragging. Which ones follows the other
    // choices, since a kind of snap turned off never happens.
    guides: boolean;
}

// Everything on, which is what a customer starts with.
export const ALL_ON: SnapSettings = {
    snapping: true,
    faces: true,
    corners: true,
    align: true,
    guides: true,
};

/**
 * @return {SnapSettings} The saved choices, each on unless saved as off, or
 * everything on when nothing is saved or it can't be read.
 */
export const loadSnapSettings = (): SnapSettings => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return {...ALL_ON};

        const saved = JSON.parse(stored) as Partial<
            Record<keyof SnapSettings, unknown>
        >;

        return {
            snapping: saved.snapping !== false,
            faces: saved.faces !== false,
            corners: saved.corners !== false,
            align: saved.align !== false,
            guides: saved.guides !== false,
        };
    } catch {
        return {...ALL_ON};
    }
};

/**
 * @param {SnapSettings} settings The choices to keep.
 */
export const saveSnapSettings = (settings: SnapSettings) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
        // Storage unavailable: the choice lasts the session.
    }
};

/**
 * What the choices let snap, as findSnaps takes it: product rules, faces
 * and corners, none of them while snapping is off.
 * @param {SnapSettings} [settings] The choices; none means everything.
 * @return {object} Whether each kind of snap is offered.
 */
export const snapsAllowed = (
    settings?: SnapSettings
): {rules: boolean; faces: boolean; corners: boolean} => {
    // No choices at all, as in a drag built without them: everything snaps.
    const {snapping, faces, corners} = settings ?? ALL_ON;

    return {
        rules: snapping,
        faces: snapping && faces,
        corners: snapping && corners,
    };
};

const NAMES: readonly (keyof SnapSettings)[] = [
    'snapping',
    'faces',
    'corners',
    'align',
    'guides',
];

/**
 * @param {unknown} name A name, such as a button's data-option.
 * @return {boolean} True when it names one of the choices.
 */
export const isSnapSettingName = (name: unknown): name is keyof SnapSettings =>
    NAMES.some((known) => known === name);

/**
 * @param {SnapSettings} settings The choices.
 * @param {string} name The one to turn on or off.
 * @return {SnapSettings} The choices with that one flipped.
 */
export const toggled = (
    settings: SnapSettings,
    name: keyof SnapSettings
): SnapSettings => {
    switch (name) {
        case 'snapping':
            return {...settings, snapping: !settings.snapping};
        case 'faces':
            return {...settings, faces: !settings.faces};
        case 'corners':
            return {...settings, corners: !settings.corners};
        case 'align':
            return {...settings, align: !settings.align};
        case 'guides':
            return {...settings, guides: !settings.guides};
    }
};

/**
 * What snapToggleFor reads of a key press.
 */
export interface SnapKeyEvent {
    key: string;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
    repeat: boolean;
    target: {tagName?: string; isContentEditable?: boolean} | null;
}

// The elements a customer types in, where a letter is just a letter.
const TYPING_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];

/**
 * The choice a key press turns on or off (CO-5857): S snapping, A lining up
 * across a gap, G the guides. Not with Ctrl, Cmd or Alt held, so Ctrl+A and
 * the like keep working, not while a key repeats, and not while typing.
 * @param {SnapKeyEvent} event The key press.
 * @return {string | null} The choice's name, or null when the press is not
 * one of these.
 */
export const snapToggleFor = (
    event: SnapKeyEvent
): 'snapping' | 'align' | 'guides' | null => {
    const {target} = event;
    if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) {
        return null;
    }
    if (
        TYPING_TAGS.includes(target?.tagName ?? '') ||
        target?.isContentEditable
    ) {
        return null;
    }

    switch (event.key.toLowerCase()) {
        case 's':
            return 'snapping';
        case 'a':
            return 'align';
        case 'g':
            return 'guides';
        default:
            return null;
    }
};
